import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Pagination,
  Row,
} from "react-bootstrap";
import toast from "react-hot-toast";
import TopBox from "../../components/TopBox";
import Asset from "../../components/Asset";
import NoResults from "../../assets/no-results.png";
import appStyles from "../../App.module.css";
import btnStyles from "../../styles/Button.module.css";
import styles from "../../styles/SupportTickets.module.css";
import { axiosInstance, axiosInstanceFormData } from "../../api/axiosDefaults";
import CreateTicketModal from "./CreateTicketModal";
import TicketPreviewModal from "./TicketPreviewModal";
import ConfirmModal from "./ConfirmModal";
import useRedirectSupport from "../../hooks/RedirectSupport";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import NeedToSignIn from "./NeedToSignin";

const ASSUMED_PAGE_SIZE = 10;
const normalizeStatus = (raw) => {
  const value = String(raw ?? "").toLowerCase();
  if (value.includes("close")) return "closed";
  return "open";
};

const stripHtmlToText = (html) => {
  const str = String(html ?? "");
  return str
    .replace(/&nbsp;/g, " ")
    // Remove tags (best-effort; backend should still sanitize/validate if needed)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const parsePurposeToCategoryLabel = (purpose) => {
  if (!purpose) return "";
  const value = String(purpose);
  return value.split("-")[0] || value;
};

const mapThreadToTicket = (thread) => {
  const id = thread?.id;
  const subject = thread?.subject ?? "";
  const category = parsePurposeToCategoryLabel(thread?.purpose) || "";
  const status = normalizeStatus(thread?.status);
  const createdAt = thread?.created_at ?? "";
  const updatedAt =
    thread?.updated_at ?? "";

  return {
    id: Number(id),
    title: subject || `Thread #${id}`,
    category,
    description: thread?.message ?? "",
    status,
    createdAt,
    updatedAt,
    replies: [],
  };
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

/** Bearer only — multipart FormData must not use JSON Content-Type. */
const bearerAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

/**
 * Takes the "next/previous page" link returned by the server and makes it usable for our app.
 *
 * - The server sometimes returns a full link (example: `https://some-domain.com/.../?page=2`).
 * - We only want the "inside the app" part (example: `/.../?page=2`).
 * - That way, clicking Next/Prev works the same in local/dev/prod without worrying about domains.
 */
const toRelativeApiUrl = (maybeAbsoluteUrl) => {
  if (!maybeAbsoluteUrl) return null;
  try {
    const parsed = new URL(maybeAbsoluteUrl, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return maybeAbsoluteUrl;
  }
};

/**
 * Figures out what page number a pagination link is pointing to.
 *
 * - When you click Next/Prev, we fetch using the link the server gave us.
 * - But we also need to update the UI (highlight the active page number).
 * - This reads `page=...` from the link so the UI knows "you're on page X".
 * - If the link does not have `page=`, we treat it as page 1 (some APIs omit `?page=1`).
 */
const parsePageFromUrl = (pageUrl) => {
  if (!pageUrl) return null;
  try {
    const parsed = new URL(pageUrl, window.location.origin);
    const value = parsed.searchParams.get("page");
    // Many paginated APIs omit `?page=1` for the first page.
    if (value == null || value === "") return 1;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
};

/**
 * Builds the list of pagination "slots" to render.
 *
 * What it's for (Pagination):
 * - Keeps the pagination bar compact on large result sets by showing:
 *   - first page, last page
 *   - current page and its immediate neighbors
 *   - ellipses where page ranges are skipped
 *
 * Output format:
 * - Numbers represent clickable page buttons.
 * - The string `"ellipsis"` represents a non-clickable gap indicator.
 */
const buildPaginationItems = ({ currentPage, totalPages }) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const clamped = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const items = [];
  for (let i = 0; i < clamped.length; i++) {
    const value = clamped[i];
    const prev = clamped[i - 1];
    if (i > 0 && value - prev > 1) items.push("ellipsis");
    items.push(value);
  }
  return items;
};

const extractMessageList = (axiosResponse) => {
  const body = axiosResponse?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.data?.results)) return body.data.results;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const mapApiMessageToReply = (msg) => {
  const id = msg?.id;
  const message = msg?.message ?? msg?.body ?? msg?.content ?? "";
  const createdAt = msg?.created_at ?? msg?.createdAt ?? "";
  const isSelf = Boolean(msg?.is_self ?? msg?.isSelf ?? false);
  const attachments = Array.isArray(msg?.attachments) ? msg.attachments : [];
  const sender = isSelf ? "You" : "Admin";

  return {
    id,
    message: String(message),
    createdAt,
    sender,
    isSelf,
    attachments,
  };
};

const hasReplyId = (reply) =>
  reply?.id !== undefined && reply?.id !== null && reply?.id !== "";

const getWrappedErrorMessage = (wrapped, fallback) => {
  const data = wrapped?.error?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    const parts = data.detail.map((item) =>
      typeof item === "string" ? item : item?.msg || JSON.stringify(item)
    );
    if (parts.length) return parts.join(" ");
  }
  if (typeof data?.message === "string") return data.message;
  return fallback;
};

const isAuthError = (wrapped) => {
  const status = wrapped?.error?.response?.status;
  return status === 401 || status === 440;
};

const emptyCreateForm = {
  title: "",
  categoryId: "",
  categoryLabel: "",
  description: "",
};

/** Max size per image attachment when raising a ticket (5 MB). */
const SUPPORT_TICKET_MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const validateSupportTicketImageFile = (file) => {
  if (!file) return "Invalid file.";
  const type = String(file.type ?? "").toLowerCase();
  if (!type.startsWith("image/")) {
    return "Only image files are allowed.";
  }
  if (file.size > SUPPORT_TICKET_MAX_IMAGE_BYTES) {
    return "Each image must be 5 MB or smaller.";
  }
  return null;
};

const FIELD_TEXT_STYLE = {
  fontSize: "var(--support-table-font-size, 0.9rem)",
  lineHeight: 1.2,
};

const SupportTicketsAuthed = () => {
  const didRunSearchEffectRef = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [ticketCache, setTicketCache] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [pageNavLoading, setPageNavLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createAttachments, setCreateAttachments] = useState([]);
  const [sorting, setSorting] = useState([]);

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const res = await axiosInstance.get("service-desk/categories/", {
        headers: authHeaders(),
        withCredentials: true,
      });
      if (res?.success === false) {
        if (!isAuthError(res)) {
          toast.error(getWrappedErrorMessage(res, "Failed to load categories."));
        }
        setTicketCategories([{ id: 5, label: "Other" }]);
        return;
      }
      const payload = res?.data?.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.results) ? payload.results : [];
      const normalized = list
        .map((item) => ({
          id: item?.id,
          label: item?.name ?? item?.label ?? item?.title ?? String(item?.id ?? ""),
        }))
        .filter((c) => c.id !== undefined && c.id !== null && c.label);
      setTicketCategories(normalized.length ? normalized : [{ id: 5, label: "Other" }]);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load categories.");
      setTicketCategories([{ id: 5, label: "Other" }]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLoadingTicketDetail, setIsLoadingTicketDetail] = useState(false);
  const [replyComposerResetKey, setReplyComposerResetKey] = useState(0);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  // Reply edit/delete — wired for future use; UI entry points are commented out in TicketPreviewModal.
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyText, setEditingReplyText] = useState("");
  const [isSavingReplyEdit, setIsSavingReplyEdit] = useState(false);
  const [showDeleteReplyConfirm, setShowDeleteReplyConfirm] = useState(false);
  const [replyToDeleteId, setReplyToDeleteId] = useState(null);
  const [isDeletingReply, setIsDeletingReply] = useState(false);

  const fetchAndSetRepliesForTicket = async (threadId) => {
    const res = await axiosInstance.get(`service-desk/threads/${threadId}/messages/`, {
      headers: authHeaders(),
      withCredentials: true,
    });
    if (res.success === false) {
      return {
        ok: false,
        message: isAuthError(res) ? "" : getWrappedErrorMessage(res, "Failed to load messages."),
      };
    }
    const list = extractMessageList(res.data);
    const replies = list.map(mapApiMessageToReply).filter((r) => hasReplyId(r));
    setSelectedTicket((current) => (current ? { ...current, replies } : current));
    return { ok: true };
  };

  const fetchTickets = async ({ searchValue, statusValue, url, page } = {}) => {
    try {
      setIsLoadingTickets(true);
      const params = {};
      const trimmedSearch = (searchValue ?? query).trim();
      if (trimmedSearch) params.search = trimmedSearch;
      const status = statusValue ?? statusFilter;
      if (status && status !== "all") params.status = status;
      if (!url && page) params.page = page;

      const response = await axiosInstance.get(url || "service-desk/threads/", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: url ? undefined : params,
        withCredentials: true,
      });

      if (response?.success === false) {
        if (!isAuthError(response)) {
          toast.error(getWrappedErrorMessage(response, "Unable to load support tickets."));
        }
        return;
      }

      const payload = response?.data?.data;
      setCount(payload?.count ?? 0);
      setNextUrl(toRelativeApiUrl(payload?.next) ?? null);
      setPrevUrl(toRelativeApiUrl(payload?.previous) ?? null);
      const results = Array.isArray(payload?.results) ? payload.results : [];

      if (!url) {
        setCurrentPage(page || 1);
      } else {
        const pageFromUrl = parsePageFromUrl(url);
        if (pageFromUrl != null) setCurrentPage(pageFromUrl);
      }
      const listing = results
        .map(mapThreadToTicket)
        .filter((ticket) => Number.isFinite(ticket.id));

      setTicketCache(listing);
      setTickets(listing);

      // If a ticket is currently open in the modal, keep its header fields
      // (including updatedAt) in sync with the refreshed list, while preserving replies.
      if (selectedTicketId != null) {
        const fresh = listing.find((t) => t.id === Number(selectedTicketId)) || null;
        if (fresh) {
          setSelectedTicket((current) =>
            current
              ? (() => {
                const next = {
                  ...current,
                  title: fresh.title,
                  category: fresh.category,
                  status: fresh.status,
                  createdAt: fresh.createdAt,
                  updatedAt: fresh.updatedAt,
                };

                // Avoid unnecessary re-renders: if nothing actually changed,
                // return the same object reference.
                const unchanged =
                  current.title === next.title &&
                  current.category === next.category &&
                  current.status === next.status &&
                  current.createdAt === next.createdAt &&
                  current.updatedAt === next.updatedAt;

                return unchanged ? current : next;
              })()
              : current
          );
        }
      }
    } catch (err) {
      // If we're unauthorized, axios interceptor will redirect; avoid flashing a toast.
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error(err?.message || "Unable to load support tickets.");
      }
      console.log(err);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  /**
   * Navigates to a new page using a server-provided pagination URL (`next`/`previous`).
   * On arrow click.
   * What it's for (Pagination):
   * - The API returns canonical URLs for `next` and `previous`; using them preserves
   *   any server-side filtering/search params already applied.
   */
  const handleNavigate = async (url) => {
    if (!url || pageNavLoading) return;
    setPageNavLoading(true);
    try {
      await fetchTickets({ url });
    } catch (err) {
      console.log(err);
      toast.error("Failed to load page.");
    } finally {
      setPageNavLoading(false);
    }
  };

  /**
   * Navigates to a specific page number by requesting `?page=N`.
   *
   * What it's for (Pagination):
   * - Used by the numbered page buttons in the pagination bar.
   * - Preserves the current search query and status filter while switching pages.
   * - On number click.
   */
  const handleNavigatePage = async (page) => {
    if (!page || pageNavLoading || page === currentPage) return;
    setPageNavLoading(true);
    try {
      await fetchTickets({ searchValue: query, statusValue: statusFilter, page });
    } catch (err) {
      console.log(err);
      toast.error("Failed to load page.");
    } finally {
      setPageNavLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Status filter: fetch immediately (server-side filtering).
    fetchTickets({ searchValue: query, statusValue: statusFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    // Search: debounce 1000ms (server-side search).
    if (!didRunSearchEffectRef.current) {
      didRunSearchEffectRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchTickets({ searchValue: query, statusValue: statusFilter });
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleCreateChange = (event) => {
    if (event.target.name === "categoryId") {
      const categoryId = event.target.value;
      const found = ticketCategories.find((category) => String(category.id) === String(categoryId));
      setCreateForm((current) => ({
        ...current,
        categoryId,
        categoryLabel: found?.label || "",
      }));
      return;
    }
    setCreateForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const handleOpenCreateModal = () => {
    const first = ticketCategories[0];
    setCreateForm({
      ...emptyCreateForm,
      categoryId: first?.id ?? "",
      categoryLabel: first?.label ?? "",
    });
    setCreateAttachments([]);
    setShowCreateModal(true);
  };

  const handleCreateAttachmentsAdd = (fileList) => {
    if (!fileList?.length) return;
    const added = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      const err = validateSupportTicketImageFile(file);
      if (err) {
        toast.error(`${file.name}: ${err}`);
        continue;
      }
      added.push(file);
    }
    if (added.length) {
      setCreateAttachments((current) => [...current, ...added]);
    }
  };

  const handleCreateAttachmentRemove = (index) => {
    setCreateAttachments((current) => current.filter((_, i) => i !== index));
  };

  const handleReplyAttachmentsAdd = (fileList) => {
    if (!fileList?.length) return;
    const added = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      const err = validateSupportTicketImageFile(file);
      if (err) {
        toast.error(`${file.name}: ${err}`);
        continue;
      }
      added.push(file);
    }
    if (added.length) {
      setReplyAttachments((current) => [...current, ...added]);
    }
  };

  const handleReplyAttachmentRemove = (index) => {
    setReplyAttachments((current) => current.filter((_, i) => i !== index));
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();

    if (!stripHtmlToText(createForm.description) || !createForm.categoryId) {
      toast.error("Category and Description are required.");
      return;
    }

    try {
      setIsSubmittingTicket(true);
      const formData = new FormData();
      formData.append("purpose", `${createForm.categoryLabel}`);
      formData.append("subject", createForm.title?.trim() || "");
      formData.append("message", String(createForm.description ?? "").trim());
      formData.append("category", String(createForm.categoryId));
      if (Array.isArray(createAttachments) && createAttachments.length) {
        createAttachments.forEach((file) => {
          if (file) formData.append("attachments", file);
        });
      }

      const response = await axiosInstanceFormData.post("service-desk/tickets/", formData, {
        withCredentials: true,
        headers: bearerAuthHeaders(),
      });
      if (response?.success === false) {
        if (!isAuthError(response)) {
          toast.error(getWrappedErrorMessage(response, "Failed to create ticket."));
        }
        return;
      }

      await fetchTickets();
      setShowCreateModal(false);
      setCreateForm(emptyCreateForm);
      setCreateAttachments([]);
      toast.success("Support ticket created.");
    } catch (err) {
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error(err?.message || "Failed to create ticket.");
      }
      console.log(err);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handleOpenTicket = async (ticketId) => {
    try {
      setSelectedTicketId(ticketId);
      setReplyComposerResetKey((k) => k + 1);
      setReplyAttachments([]);
      setEditingReplyId(null);
      setEditingReplyText("");
      setShowDetailModal(true);
      setIsLoadingTicketDetail(true);
      const detail = ticketCache.find((ticket) => ticket.id === Number(ticketId)) || null;
      setSelectedTicket(detail);

      const { ok, message } = await fetchAndSetRepliesForTicket(ticketId);
      if (!ok && message) {
        toast.error(message);
      }
    } catch (err) {
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error(err?.message || "Unable to load ticket details.");
      }
      console.log(err);
    } finally {
      setIsLoadingTicketDetail(false);
    }
  };

  const handleReply = async (event, messageHtmlOverride) => {
    event?.preventDefault?.();
    const messageHtml = messageHtmlOverride ?? "";

    if (!stripHtmlToText(messageHtml)) {
      toast.error("Please write your reply.");
      return;
    }

    if (!selectedTicketId) {
      toast.error("No ticket selected.");
      return;
    }

    try {
      setIsSubmittingReply(true);
      const formData = new FormData();
      const messageHtmlString = String(messageHtml ?? "").trim();
      formData.append("message", messageHtmlString);
      if (Array.isArray(replyAttachments) && replyAttachments.length) {
        replyAttachments.forEach((file) => {
          if (file) formData.append("attachments", file);
        });
      }

      const res = await axiosInstanceFormData.post(
        `service-desk/threads/${selectedTicketId}/messages/`,
        formData,
        {
          withCredentials: true,
          headers: bearerAuthHeaders(),
        }
      );
      if (res.success === false) {
        if (!isAuthError(res)) {
          toast.error(getWrappedErrorMessage(res, "Failed to submit reply."));
        }
        return;
      }
      setReplyComposerResetKey((k) => k + 1);
      setReplyAttachments([]);
      const refreshed = await fetchAndSetRepliesForTicket(selectedTicketId);
      if (!refreshed.ok) {
        toast.error(refreshed.message || "Reply sent but messages could not be refreshed.");
      } else {
        toast.success("Reply added.");
      }

      // Refresh the ticket list so the server-side thread `updated_at` is reflected
      // in the "Updated at" column for this ticket.
      try {
        await fetchTickets({ searchValue: query, statusValue: statusFilter, page: currentPage });
      } catch (e) {
        toast.error("Reply sent, but ticket list could not be refreshed.");
      }
    } catch (err) {
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error("Failed to submit reply.");
      }
      console.log(err);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleStartEditReply = (event, reply) => {
    event.stopPropagation();
    setEditingReplyId(reply.id);
    setEditingReplyText(reply.message);
  };

  const handleCancelEditReply = () => {
    setEditingReplyId(null);
    setEditingReplyText("");
  };

  const handleSaveEditReply = async (event) => {
    event.preventDefault();
    if (!selectedTicketId || !editingReplyId) return;
    if (!stripHtmlToText(editingReplyText)) {
      toast.error("Reply cannot be empty.");
      return;
    }

    try {
      setIsSavingReplyEdit(true);
      const res = await axiosInstance.patch(
        `service-desk/threads/${selectedTicketId}/messages/${editingReplyId}/`,
        { message: String(editingReplyText ?? "").trim() },
        { headers: authHeaders(), withCredentials: true }
      );
      if (res.success === false) {
        if (!isAuthError(res)) {
          toast.error(getWrappedErrorMessage(res, "Failed to edit reply."));
        }
        return;
      }
      setSelectedTicket((current) =>
        current
          ? {
              ...current,
              replies: current.replies.map((reply) =>
                reply.id === editingReplyId
                  ? { ...reply, message: editingReplyText.trim() }
                  : reply
              ),
            }
          : current
      );
      toast.success("Reply updated.");
      handleCancelEditReply();
    } catch (err) {
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error("Failed to edit reply.");
      }
      console.log(err);
    } finally {
      setIsSavingReplyEdit(false);
    }
  };

  const handleAskDeleteReply = (event, replyId) => {
    event.stopPropagation();
    setReplyToDeleteId(replyId);
    setShowDeleteReplyConfirm(true);
  };

  const handleConfirmDeleteReply = async () => {
    if (!selectedTicketId || !replyToDeleteId) return;

    try {
      setIsDeletingReply(true);
      const res = await axiosInstance.delete(
        `service-desk/threads/${selectedTicketId}/messages/${replyToDeleteId}/`,
        { headers: authHeaders(), withCredentials: true }
      );
      if (res.success === false) {
        if (!isAuthError(res)) {
          toast.error(getWrappedErrorMessage(res, "Failed to delete reply."));
        }
        return;
      }
      setSelectedTicket((current) =>
        current
          ? {
              ...current,
              replies: current.replies.filter((reply) => reply.id !== replyToDeleteId),
            }
          : current
      );
      toast.success("Reply deleted.");
      setShowDeleteReplyConfirm(false);
      setReplyToDeleteId(null);
    } catch (err) {
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error("Failed to delete reply.");
      }
      console.log(err);
    } finally {
      setIsDeletingReply(false);
    }
  };

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [ticketToCloseId, setTicketToCloseId] = useState(null);
  const [isClosingTicket, setIsClosingTicket] = useState(false);

  const handleAskCloseTicket = useCallback((event, ticketId) => {
    event.stopPropagation();
    setTicketToCloseId(ticketId);
    setShowCloseConfirm(true);
  }, []);

  const handleConfirmCloseTicket = async () => {
    if (!ticketToCloseId) return;

    try {
      setIsClosingTicket(true);
      const response = await axiosInstance.put(
        `service-desk/threads/${ticketToCloseId}/`,
        { status: "closed" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          withCredentials: true,
        }
      );

      console.log(response);

      if (response?.success === false) {
        if (!isAuthError(response)) {
          toast.error(getWrappedErrorMessage(response, "Failed to close ticket."));
        }
        return;
      }

      await fetchTickets();
      toast.success("Ticket closed.");
      setShowCloseConfirm(false);
      setTicketToCloseId(null);
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || "Failed to close ticket.";
      if (!(err?.response?.status === 401 || err?.response?.status === 440)) {
        toast.error(message);
      }
      console.log(err);
    } finally {
      setIsClosingTicket(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: (info) => info.getValue(),
      },
      {
        id: "subject",
        accessorKey: "title",
        header: "Subject",
        cell: (info) => <div className={styles.Content4}>{info.getValue()}</div>,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (info) => info.getValue(),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: "Created at",
        sortingFn: (rowA, rowB, columnId) => {
          const av = rowA.getValue(columnId);
          const bv = rowB.getValue(columnId);

          const aEmpty = av === null || av === undefined || av === "";
          const bEmpty = bv === null || bv === undefined || bv === "";
          if (aEmpty && bEmpty) return 0;
          if (aEmpty) return 1;
          if (bEmpty) return -1;

          const at = Date.parse(String(av));
          const bt = Date.parse(String(bv));
          const aValid = Number.isFinite(at);
          const bValid = Number.isFinite(bt);
          if (aValid && bValid) return at - bt;

          return String(av).localeCompare(String(bv));
        },
        cell: (info) => info.getValue(),
      },
      {
        id: "updatedAt",
        accessorKey: "updatedAt",
        header: "Updated at",
        sortingFn: (rowA, rowB, columnId) => {
          const av = rowA.getValue(columnId);
          const bv = rowB.getValue(columnId);

          const aEmpty = av === null || av === undefined || av === "";
          const bEmpty = bv === null || bv === undefined || bv === "";
          if (aEmpty && bEmpty) return 0;
          if (aEmpty) return 1;
          if (bEmpty) return -1;

          const at = Date.parse(String(av));
          const bt = Date.parse(String(bv));
          const aValid = Number.isFinite(at);
          const bValid = Number.isFinite(bt);
          if (aValid && bValid) return at - bt;

          return String(av).localeCompare(String(bv));
        },
        cell: (info) => info.getValue() || "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => (
          <Badge variant={info.getValue() === "open" ? "success" : "secondary"}>
            {info.getValue()}
          </Badge>
        ),
      },
      {
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: ({ row }) => {
          const ticket = row.original;
          return ticket.status === "open" ? (
            <Button
              variant="outline-danger"
              className={styles.ActionButton}
              onClick={(event) => handleAskCloseTicket(event, ticket.id)}
            >
              Close
            </Button>
          ) : (
            <span className="text-muted">-</span>
          );
        },
      },
    ],
    [handleAskCloseTicket]
  );

  const table = useReactTable({
    data: tickets,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const renderSortIndicator = (column) => {
    const isSorted = column.getIsSorted(); // 'asc' | 'desc' | false
    const icon = !isSorted ? "↕" : isSorted === "asc" ? "↑" : "↓";
    return (
      <span
        className={`${styles.SortIndicator} ${isSorted ? styles.SortIndicatorActive : ""}`}
      >
        {icon}
      </span>
    );
  };

  const renderTicketList = () => {
    if (isLoadingTickets) {
      return (
        <Container className={appStyles.Content}>
          <Asset spinner />
        </Container>
      );
    }

    if (!tickets.length) {
      return (
        <Container className={appStyles.Content}>
          <Asset src={NoResults} message="No Tickets" />
        </Container>
      );
    }

    const totalPages = Math.max(1, Math.ceil(count / ASSUMED_PAGE_SIZE));
    const pageItems = buildPaginationItems({ currentPage, totalPages });
    const showPagination = totalPages > 1;

    return (
      <>
        <div className={styles.TableResponsive}>
          <table className={`table table-hover mt-2 mb-0 ${styles.TicketsTable}`}>
            <thead className={styles.TableHeader}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        role={canSort ? "button" : undefined}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        style={{
                          cursor: canSort ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort ? renderSortIndicator(header.column) : null}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`${styles.TableRow} ${styles.RowHover}`}
                  onClick={() => handleOpenTicket(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={FIELD_TEXT_STYLE}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showPagination && (
          <div
            className={`d-flex justify-content-center align-items-center flex-wrap mt-3 ${styles.paginationRoot}`}
          >
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => handleNavigate(prevUrl)}
                disabled={prevUrl == null || pageNavLoading}
              />
              {pageItems.map((item, idx) =>
                item === "ellipsis" ? (
                  <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                ) : (
                  <Pagination.Item
                    key={`page-${item}`}
                    active={item === currentPage}
                    disabled={pageNavLoading}
                    onClick={() => handleNavigatePage(item)}
                  >
                    {item}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                onClick={() => handleNavigate(nextUrl)}
                disabled={nextUrl == null || pageNavLoading}
              />
            </Pagination>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.PageRoot}>
      <div className="mx-md-5">
        <TopBox title="Support" />
      </div>

      <Row className="mt-2">
        <Col className="text-center" xs={12} md={{ span: 6, offset: 3 }}>
          <Form
            className={`${styles.SearchBar} mt-3`}
            onSubmit={(event) => event.preventDefault()}
          >
            <Form.Control
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              type="text"
              className={styles.InputBar}
              placeholder="Search by ticket title, id or category"
            />
          </Form>
        </Col>
      </Row>

      <Row className="mb-3 mt-2 px-2">
        <Col md={{ span: 10, offset: 1 }}>
          <Card className={appStyles.Content}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="mb-2 mb-md-0">Tickets Raised</h5>
                <div className="d-flex align-items-center flex-wrap">
                  <Form.Group className="mb-2 mb-md-0 mr-2">
                    <Form.Control
                      as="select"
                      aria-label="Status"
                      className={styles.StatusSelect}
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">Show All</option>
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                    </Form.Control>
                  </Form.Group>
                  <Button
                    className={`${btnStyles.Button} ${btnStyles.Blue} ${styles.RaiseButton} mb-2 mb-md-0`}
                    onClick={handleOpenCreateModal}
                  >
                    Raise a New Ticket
                  </Button>
                </div>
              </div>
              {renderTicketList()}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CreateTicketModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        createForm={createForm}
        onChange={handleCreateChange}
        onSubmit={handleCreateTicket}
        isSubmitting={isSubmittingTicket}
        categories={ticketCategories}
        isLoadingCategories={isLoadingCategories}
        attachmentFiles={createAttachments}
        onAttachmentsAdd={handleCreateAttachmentsAdd}
        onAttachmentRemove={handleCreateAttachmentRemove}
      />

      <TicketPreviewModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        isLoading={isLoadingTicketDetail}
        selectedTicket={selectedTicket}
        editingReplyId={editingReplyId}
        editingReplyText={editingReplyText}
        setEditingReplyText={setEditingReplyText}
        isSavingReplyEdit={isSavingReplyEdit}
        onStartEditReply={handleStartEditReply}
        onCancelEditReply={handleCancelEditReply}
        onSaveEditReply={handleSaveEditReply}
        onAskDeleteReply={handleAskDeleteReply}
        replyComposerResetKey={replyComposerResetKey}
        isSubmittingReply={isSubmittingReply}
        onReply={handleReply}
        replyAttachmentFiles={replyAttachments}
        onReplyAttachmentsAdd={handleReplyAttachmentsAdd}
        onReplyAttachmentRemove={handleReplyAttachmentRemove}
      />

      <ConfirmModal
        show={showDeleteReplyConfirm}
        onHide={() => setShowDeleteReplyConfirm(false)}
        title="Confirm Delete"
        body="Do you want to delete this reply?"
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDeleteReply}
        isConfirming={isDeletingReply}
      />

      <ConfirmModal
        show={showCloseConfirm}
        onHide={() => setShowCloseConfirm(false)}
        title="Confirm Close"
        body="Do you want to close the ticket?"
        confirmLabel="Close Ticket"
        confirmVariant="danger"
        onConfirm={handleConfirmCloseTicket}
        isConfirming={isClosingTicket}
      />
    </div>
  );
};

const SupportTickets = () => {
  useRedirectSupport();
  const currentUser = useCurrentUser();
  const hasAuth = Boolean(currentUser);

  return hasAuth ? <SupportTicketsAuthed /> : <NeedToSignIn />;
};

export default SupportTickets;
